/**
 * Traduction des messages d'erreur Supabase Auth en français
 * Couvre les erreurs d'authentification, réseau, rate limiting et sessions
 */

const AUTH_ERROR_TRANSLATIONS: Record<string, string> = {
  // ============ Erreurs de mot de passe ============
  'Password is known to be weak and easy to guess, please choose a different one': 
    'Ce mot de passe a été détecté dans des fuites de données connues. Pour votre sécurité, veuillez en choisir un différent.',
  'Password should be at least': 
    'Le mot de passe doit contenir au moins 6 caractères.',
  'Password should contain at least one character of each': 
    'Le mot de passe doit contenir au moins une majuscule, une minuscule et un chiffre.',
  'New password should be different from the old password': 
    'Le nouveau mot de passe doit être différent de l\'ancien.',
  
  // ============ Erreurs de connexion ============
  'Invalid login credentials': 
    'Email ou mot de passe incorrect.',
  'Email not confirmed': 
    'Veuillez confirmer votre email avant de vous connecter.',
  'Invalid email or password': 
    'Email ou mot de passe invalide.',
  
  // ============ Erreurs d'inscription ============
  'User already registered': 
    'Un compte existe déjà avec cet email.',
  'Signup requires a valid password': 
    'Un mot de passe valide est requis pour l\'inscription.',
  'Unable to validate email address': 
    'Impossible de valider cette adresse email.',
  'A user with this email address has already been registered': 
    'Un utilisateur avec cette adresse email existe déjà.',
  
  // ============ Rate limiting ============
  'Email rate limit exceeded': 
    'Trop de tentatives. Veuillez réessayer dans quelques minutes.',
  'For security purposes, you can only request this after': 
    'Pour des raisons de sécurité, veuillez patienter avant de réessayer.',
  'Rate limit exceeded': 
    'Trop de requêtes. Veuillez patienter quelques instants.',
  'Too many requests': 
    'Trop de requêtes. Veuillez réessayer dans quelques minutes.',
  'Request rate limit reached': 
    'Limite de requêtes atteinte. Veuillez patienter.',
  'over_email_send_rate_limit': 
    'Trop d\'emails envoyés. Veuillez patienter quelques minutes.',
  'over_request_rate_limit': 
    'Trop de requêtes. Veuillez réessayer plus tard.',
  
  // ============ Erreurs de token/lien ============
  'Token has expired or is invalid': 
    'Ce lien a expiré ou est invalide. Veuillez en demander un nouveau.',
  'Email link is invalid or has expired': 
    'Ce lien a expiré ou est invalide. Veuillez en demander un nouveau.',
  'OTP has expired': 
    'Le code de vérification a expiré. Veuillez en demander un nouveau.',
  'Invalid OTP': 
    'Code de vérification invalide.',
  'Token is expired or invalid': 
    'Le lien a expiré ou est invalide.',
  
  // ============ Erreurs de session ============
  'Session expired': 
    'Votre session a expiré. Veuillez vous reconnecter.',
  'Refresh Token Not Found': 
    'Session expirée. Veuillez vous reconnecter.',
  'Invalid Refresh Token': 
    'Session invalide. Veuillez vous reconnecter.',
  'Auth session missing': 
    'Session manquante. Veuillez vous reconnecter.',
  'JWT expired': 
    'Session expirée. Veuillez vous reconnecter.',
  'Invalid JWT': 
    'Session invalide. Veuillez vous reconnecter.',
  'User not found': 
    'Utilisateur non trouvé.',
  'No user found': 
    'Aucun utilisateur trouvé.',
  
  // ============ Erreurs réseau ============
  'Failed to fetch': 
    'Erreur de connexion. Vérifiez votre connexion internet.',
  'Network request failed': 
    'Erreur réseau. Vérifiez votre connexion internet.',
  'NetworkError': 
    'Erreur réseau. Veuillez vérifier votre connexion.',
  'net::ERR_': 
    'Erreur de connexion au serveur.',
  'ECONNREFUSED': 
    'Impossible de se connecter au serveur.',
  'ETIMEDOUT': 
    'La connexion a expiré. Veuillez réessayer.',
  'timeout': 
    'Délai d\'attente dépassé. Veuillez réessayer.',
  'Load failed': 
    'Échec du chargement. Vérifiez votre connexion.',
  'The Internet connection appears to be offline': 
    'Vous semblez être hors ligne. Vérifiez votre connexion internet.',
  'A network error occurred': 
    'Une erreur réseau s\'est produite. Veuillez réessayer.',
  
  // ============ Erreurs serveur ============
  'Database error': 
    'Erreur serveur. Veuillez réessayer plus tard.',
  'Internal server error': 
    'Erreur interne du serveur. Veuillez réessayer.',
  'Service unavailable': 
    'Service temporairement indisponible. Veuillez réessayer plus tard.',
  'Bad gateway': 
    'Erreur de serveur. Veuillez réessayer plus tard.',
  '500': 
    'Erreur serveur. Veuillez réessayer.',
  '502': 
    'Service temporairement indisponible.',
  '503': 
    'Service en maintenance. Veuillez réessayer plus tard.',
  '504': 
    'Le serveur ne répond pas. Veuillez réessayer.',
  
  // ============ Erreurs de validation ============
  'Invalid email': 
    'Adresse email invalide.',
  'Email is required': 
    'L\'adresse email est requise.',
  'Password is required': 
    'Le mot de passe est requis.',
  'Validation failed': 
    'Erreur de validation. Vérifiez les informations saisies.',
  
  // ============ Erreurs OAuth/SSO ============
  'OAuth error': 
    'Erreur d\'authentification. Veuillez réessayer.',
  'Provider error': 
    'Erreur du fournisseur d\'authentification.',
  'Access denied': 
    'Accès refusé.',
  
  // ============ Autres erreurs ============
  'Something went wrong': 
    'Une erreur s\'est produite. Veuillez réessayer.',
  'An error occurred': 
    'Une erreur s\'est produite.',
  'Unknown error': 
    'Une erreur inconnue s\'est produite.',
  'Unexpected error': 
    'Erreur inattendue. Veuillez réessayer.',
};

/**
 * Traduit un message d'erreur Supabase Auth en français
 * @param message - Le message d'erreur original en anglais
 * @returns Le message traduit en français, ou le message original si pas de traduction
 */
export function translateAuthError(message: string): string {
  if (!message) return 'Une erreur s\'est produite.';
  
  // Cherche une correspondance exacte ou partielle
  for (const [key, translation] of Object.entries(AUTH_ERROR_TRANSLATIONS)) {
    if (message.toLowerCase().includes(key.toLowerCase())) {
      return translation;
    }
  }
  
  return message; // Retourne le message original si pas de traduction
}
